// «Сохранить беседу в базу?» — один диалог на два случая: кнопка «запиши»
// в шапке окна и подтверждение при закрытии несохранённой беседы. Различает
// их наличие onDiscard: «Не сохранять» имеет смысл только при закрытии, в
// обычном «запиши» такая кнопка была бы просто вторым способом отменить.
//
// Заметку собирает сервер и только из выводов агента (HUB_CONTRACT_2.md):
// здесь оператор задаёт лишь название и папку. База — не журнал, а результат,
// поэтому диалог честно предупреждает, что реплики и вывод инструментов
// в заметку не поедут.

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, ApiError } from '../api'
import type { TreeNode } from '../protocol'
import { describeError, useAppStore } from '../store/appStore'
import './savenote.css'

/** Куда сервер кладёт заметку, если папку не задали (HUB_CONTRACT_2.md). */
const DEFAULT_FOLDER = 'работа/беседы'

/** Плоский список каталогов базы — подсказкой к полю «папка». */
function folderPaths(nodes: TreeNode[], out: string[] = []): string[] {
  for (const node of nodes) {
    if (node.kind !== 'folder') continue
    out.push(node.path)
    folderPaths(node.children, out)
  }
  return out
}

interface Props {
  sessionId: string
  onSaved(notePath: string): void
  onClose(): void
  /** «Не сохранять» при закрытии окна */
  onDiscard?(): void
}

export default function SaveNoteDialog({ sessionId, onSaved, onClose, onDiscard }: Props) {
  const [title, setTitle] = useState('')
  const [folder, setFolder] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tree = useAppStore((s) => s.tree)
  const refreshTree = useAppStore((s) => s.refreshTree)
  const folders = useMemo(() => folderPaths(tree), [tree])

  const listId = useId()
  const firstRef = useRef<HTMLInputElement>(null)

  useEffect(() => { firstRef.current?.focus() }, [])

  // Escape = «Отмена»: окно беседы остаётся на месте, в базе ничего не создаётся.
  // Пока запрос летит, отменять нечего — заметка уже может быть записана.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || busy) return
      ev.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [busy, onClose])

  const save = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const note = await api.saveNote(sessionId, {
        title: title.trim() || undefined,
        folder: folder.trim() || undefined,
      })
      // Дерево слева перечитываем сами: заметка только что появилась на диске,
      // а индекс на сервере обновляется по mtime и сам о ней не расскажет.
      void refreshTree()
      onSaved(note.path)
      // busy не снимаем: компонент уже размонтируется вместе с диалогом
    } catch (e) {
      // Маршрут сохранения выкатывается отдельно — 404 объясняем словами,
      // а не общим «сервер не знает такого адреса».
      setError(e instanceof ApiError && e.status === 404
        ? 'Сервер пока не умеет сохранять беседы в базу — обновите agentbox.'
        : describeError(e))
      setBusy(false)
    }
  }

  const onEnter = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') { ev.preventDefault(); void save() }
  }

  return createPortal(
    <div
      className="ab-savenote"
      role="presentation"
      // Клик мимо карточки — та же «Отмена». Останавливаем всплытие, иначе
      // клик уйдёт в панель и та перехватит фокус уже закрытого окна.
      onMouseDown={(ev) => {
        ev.stopPropagation()
        if (ev.target === ev.currentTarget && !busy) onClose()
      }}
    >
      <div
        className="ab-savenote__card"
        role="dialog"
        aria-modal="true"
        aria-label="Сохранить беседу в базу?"
      >
        <div className="ab-savenote__title">Сохранить беседу в базу?</div>
        <div className="ab-savenote__text">
          В заметку попадут только выводы агента: ваши реплики, вызовы инструментов
          и их вывод в базу не идут.
        </div>

        <label className="ab-savenote__field">
          <span className="ab-savenote__label">название</span>
          <input
            ref={firstRef}
            className="ab-savenote__input"
            value={title}
            placeholder="возьмём из беседы"
            disabled={busy}
            onChange={(ev) => setTitle(ev.target.value)}
            onKeyDown={onEnter}
          />
        </label>

        <label className="ab-savenote__field">
          <span className="ab-savenote__label">папка</span>
          <input
            className="ab-savenote__input"
            list={listId}
            value={folder}
            placeholder={DEFAULT_FOLDER}
            disabled={busy}
            onChange={(ev) => setFolder(ev.target.value)}
            onKeyDown={onEnter}
          />
          <datalist id={listId}>
            {folders.map((path) => <option key={path} value={path} />)}
          </datalist>
        </label>

        {/* Тостов здесь нет: причина отказа должна остаться в диалоге,
            иначе кнопка выглядит сломанной. */}
        {error ? <div className="ab-savenote__err" role="alert">{error}</div> : null}

        <div className="ab-savenote__row">
          <button
            type="button"
            className="ab-savenote__ok"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? 'сохраняю…' : 'Сохранить в базу'}
          </button>
          {onDiscard ? (
            <button
              type="button"
              className="ab-savenote__btn"
              title="закрыть окно, ничего не создавая в базе"
              disabled={busy}
              onClick={onDiscard}
            >
              Не сохранять
            </button>
          ) : null}
          <span className="ab-savenote__spacer" />
          <button
            type="button"
            className="ab-savenote__cancel"
            disabled={busy}
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
